// =============================================================================
// AuthorPhotoGenerator — dwuetapowe generowanie zdjęcia autora (krok "Autor info").
//
//   Etap 1 „Zaproponuj prompt"  → Opus 4.8 pisze prompt z danych mapy → EDYTOWALNY.
//   Etap 2 „Generuj obrazki"    → prompt zastyga (read-only, zapisany w map_persona),
//                                 pod nim propozycje z FAL Seedream 4.5 do wyboru.
//
// Zaznaczone propozycje lądują w istniejącej bibliotece zdjęć autora (bucket
// 'author-photos') — tam działa już pobieranie i usuwanie.
// =============================================================================

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sparkles, Loader2, Wand2, Check, RotateCcw, Save, AlertTriangle, Camera,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  buildAuthorPhotoPrompt,
  generateAuthorPhotos,
  proposalToFile,
  type AuthorPhotoSize,
} from "@/services/uniquePages/authorPhotoGenerator";
import { uploadAuthorPhoto } from "@/services/uniquePages/authorPhotos";
import type { ThematicMap } from "@/types/thematicMaps";

const COUNT_OPTIONS = [4, 6, 8];

interface Props {
  map: ThematicMap;
  /** Biblioteka niepusta → generator domyślnie zwinięty („jak jest zdjęcie, olewamy"). */
  hasPhotos: boolean;
  /** Prompt UŻYTY do generowania (z map_persona) — null = jeszcze nie generowano. */
  savedPrompt: string | null;
  /** Zapis / wyczyszczenie użytego promptu (null = wyczyść). */
  onPromptChange: (prompt: string | null) => Promise<void>;
  /** Odśwież bibliotekę po zapisaniu wybranych propozycji. */
  onSaved: () => void | Promise<void>;
}

export function AuthorPhotoGenerator({ map, hasPhotos, savedPrompt, onPromptChange, onSaved }: Props) {
  const { toast } = useToast();

  const [draft, setDraft] = useState<string | null>(null);
  const [proposals, setProposals] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [count, setCount] = useState(6);
  const [size, setSize] = useState<AuthorPhotoSize>("square");
  const [building, setBuilding] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);

  const busy = building || generating || saving;
  const collapsed = hasPhotos && !forceOpen;

  const handleBuildPrompt = async () => {
    setBuilding(true);
    try {
      const res = await buildAuthorPhotoPrompt(map.id);
      setDraft(res.prompt);
      if (res.source === "fallback") {
        toast({
          title: "Użyto promptu zapasowego",
          description: "Model nie zwrócił poprawnej odpowiedzi — możesz poprawić prompt ręcznie przed generowaniem.",
        });
      }
    } catch (e) {
      toast({
        title: "Nie udało się zbudować promptu",
        description: e instanceof Error ? e.message : "Nieznany błąd",
        variant: "destructive",
      });
    } finally {
      setBuilding(false);
    }
  };

  const handleGenerate = async () => {
    const prompt = (savedPrompt ?? draft ?? "").trim();
    if (!prompt) return;
    setGenerating(true);
    setProposals([]);
    setSelected(new Set());
    try {
      // Zapisujemy prompt DOPIERO tutaj — czyli po ewentualnej ręcznej edycji.
      // Od tego momentu jest read-only i widoczny obok wygenerowanych zdjęć.
      if (!savedPrompt) await onPromptChange(prompt);

      const res = await generateAuthorPhotos({
        prompt,
        count,
        imageSize: size,
        onImage: (src) => setProposals((p) => [...p, src]),
      });

      if (res.ok === 0) {
        toast({
          title: "Nie wygenerowano żadnego zdjęcia",
          description: res.errors[0] || "FAL nie zwrócił obrazków.",
          variant: "destructive",
        });
      } else if (res.failed > 0) {
        toast({
          title: `Gotowe ${res.ok} z ${count}`,
          description: res.errors[0],
        });
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleClearPrompt = async () => {
    setDraft(null);
    setProposals([]);
    setSelected(new Set());
    await onPromptChange(null);
  };

  const toggle = (i: number) =>
    setSelected((s) => {
      const next = new Set(s);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });

  const handleSaveSelected = async () => {
    const chosen = [...selected];
    if (!chosen.length) return;
    setSaving(true);
    const errors: string[] = [];
    let ok = 0;
    try {
      for (const i of chosen) {
        try {
          const file = await proposalToFile(proposals[i], i);
          const res = await uploadAuthorPhoto(map.id, file);
          if (res.ok) ok++;
          else errors.push(res.error || "błąd zapisu");
        } catch (e) {
          errors.push(e instanceof Error ? e.message : "błąd zapisu");
        }
      }
      setProposals((p) => p.filter((_, i) => !selected.has(i)));
      setSelected(new Set());
      await onSaved();
      toast({
        title: `Zapisano ${ok} zdjęć w bibliotece`,
        description: errors.length ? `Błędy: ${errors.join("; ")}` : undefined,
        variant: errors.length ? "destructive" : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  // --- Stan D: biblioteka niepusta → zwinięte -------------------------------
  if (collapsed) {
    return (
      <div className="space-y-2 rounded-md border border-border/60 bg-muted/20 p-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Camera className="h-3.5 w-3.5" />
            Zdjęcia autora są już w bibliotece — generator wyłączony.
          </span>
          <Button size="sm" variant="ghost" className="h-6 shrink-0 text-xs" onClick={() => setForceOpen(true)}>
            Wygeneruj mimo to
          </Button>
        </div>
        {savedPrompt && <PromptReadOnly prompt={savedPrompt} />}
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-md border border-border/60 p-3">
      <div>
        <h4 className="flex items-center gap-2 text-sm font-semibold">
          <Sparkles className="h-4 w-4 text-primary" />
          Wygeneruj zdjęcie autora (AI)
        </h4>
        <p className="text-xs text-muted-foreground">
          Krok 1: model Opus 4.8 pisze opis zdjęcia z danych mapy (odbiorcy, opis serwisu, profil autora) — możesz go
          poprawić. Krok 2: FAL Seedream 4.5 generuje propozycje, a Ty wybierasz, które zapisać.
        </p>
      </div>

      {/* --- Stan A: nic jeszcze nie ma ------------------------------------ */}
      {!savedPrompt && draft === null && (
        <Button size="sm" onClick={handleBuildPrompt} disabled={busy}>
          {building ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1.5 h-3.5 w-3.5" />}
          1. Zaproponuj prompt (Opus 4.8)
        </Button>
      )}

      {/* --- Stan B: draft promptu — edytowalny ---------------------------- */}
      {!savedPrompt && draft !== null && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">Prompt (możesz poprawić przed generowaniem)</span>
            <Badge variant="outline" className="text-[10px]">
              edytowalny
            </Badge>
          </div>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="min-h-[130px] font-mono text-xs"
            disabled={busy}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Select value={String(count)} onValueChange={(v) => setCount(Number(v))} disabled={busy}>
              <SelectTrigger className="h-8 w-[130px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNT_OPTIONS.map((n) => (
                  <SelectItem key={n} value={String(n)} className="text-xs">
                    {n} propozycji
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={size} onValueChange={(v) => setSize(v as AuthorPhotoSize)} disabled={busy}>
              <SelectTrigger className="h-8 w-[170px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="square" className="text-xs">kwadrat 512×512</SelectItem>
                <SelectItem value="square_hd" className="text-xs">kwadrat 1024×1024</SelectItem>
              </SelectContent>
            </Select>

            <Button size="sm" onClick={handleGenerate} disabled={busy || !draft.trim()}>
              {generating ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
              2. Generuj obrazki
            </Button>
            <Button size="sm" variant="ghost" className="text-xs" onClick={() => setDraft(null)} disabled={busy}>
              Wyczyść
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Po kliknięciu „Generuj obrazki" prompt zostaje zapisany i przestaje być edytowalny — widać wtedy, czym
            wygenerowano zdjęcia. Koszt: ok. $0,03 za obrazek.
          </p>
        </div>
      )}

      {/* --- Stan C: prompt użyty (read-only) + propozycje ------------------ */}
      {savedPrompt && (
        <div className="space-y-2">
          <PromptReadOnly prompt={savedPrompt} />
          <div className="flex flex-wrap items-center gap-2">
            <Select value={String(count)} onValueChange={(v) => setCount(Number(v))} disabled={busy}>
              <SelectTrigger className="h-8 w-[130px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNT_OPTIONS.map((n) => (
                  <SelectItem key={n} value={String(n)} className="text-xs">
                    {n} propozycji
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={handleGenerate} disabled={busy}>
              {generating ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
              Generuj ponownie
            </Button>
            <Button size="sm" variant="ghost" className="text-xs" onClick={handleClearPrompt} disabled={busy}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Wyczyść prompt
            </Button>
          </div>
        </div>
      )}

      {/* --- Propozycje ---------------------------------------------------- */}
      {(generating || proposals.length > 0) && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">
              Propozycje {generating ? `(${proposals.length}/${count} gotowych…)` : `— zaznacz te, które chcesz zapisać`}
            </span>
            {proposals.length > 0 && (
              <Button size="sm" onClick={handleSaveSelected} disabled={busy || selected.size === 0}>
                {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
                Zapisz zaznaczone ({selected.size})
              </Button>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {proposals.map((src, i) => {
              const isSel = selected.has(i);
              return (
                <button
                  key={`${i}-${src.slice(-24)}`}
                  type="button"
                  onClick={() => toggle(i)}
                  disabled={saving}
                  className={`group relative overflow-hidden rounded-md border-2 transition-colors ${
                    isSel ? "border-primary" : "border-border/60 hover:border-primary/40"
                  }`}
                >
                  <img src={src} alt={`Propozycja ${i + 1}`} className="aspect-square w-full object-cover" />
                  {isSel && (
                    <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <Check className="h-3 w-3" />
                    </span>
                  )}
                </button>
              );
            })}
            {generating &&
              Array.from({ length: Math.max(0, count - proposals.length) }).map((_, i) => (
                <div
                  key={`skeleton-${i}`}
                  className="flex aspect-square w-full items-center justify-center rounded-md border border-dashed border-border/60"
                >
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ))}
          </div>

          {!generating && proposals.length > 0 && (
            <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <AlertTriangle className="h-3 w-3" />
              Niezapisane propozycje znikną po odświeżeniu strony. Zapisane trafiają do biblioteki jako lekki JPEG.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Prompt użyty do generowania — pokazany „obok" zdjęć, celowo NIE do edycji. */
function PromptReadOnly({ prompt }: { prompt: string }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-medium text-muted-foreground">Użyty prompt</span>
        <Badge variant="outline" className="text-[10px] text-muted-foreground">
          tylko do odczytu
        </Badge>
      </div>
      <p className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded bg-muted px-2 py-1.5 font-mono text-[11px] leading-relaxed text-muted-foreground">
        {prompt}
      </p>
    </div>
  );
}

export default AuthorPhotoGenerator;
